/**
 * Print + Share pure helpers — testable without expo-print/expo-sharing.
 *
 * Phase 4.4: extracted from print.ts so the pure functions can be unit
 * tested in the default Node pool (no native module resolution).
 *
 * The platform-aware functions (printWorksheet, downloadPdf)
 * live in print.ts and require expo-print/expo-sharing at runtime.
 * (WhatsApp share helpers dihapus 2026-08-19.)
 */

import type { PrintResult } from './print-types';

// Re-export the result type for callers.
export type { PrintResult };

// ----------------------------------------------------------------------------
// Filename builder
// ----------------------------------------------------------------------------

/** Build a human-readable filename for a worksheet PDF. */
export function buildPdfFilename(worksheetName: string, id: number): string {
  const slug = worksheetName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `funpaper-${slug || 'worksheet'}-${id}.pdf`;
}

// ----------------------------------------------------------------------------
// Batch print (Phase 4.5 — "Cetak Semua")
// ----------------------------------------------------------------------------

export interface BatchPrintItem {
  id: number;
  name: string;
  pdfUrl: string | null;
}

export interface BatchPrintResult {
  total: number;
  printed: number;
  skipped: number;
  errors: { id: number; name: string; error: string }[];
}

export type BatchPrintAction = 'print' | 'download';

/**
 * Build the user-facing summary for a batch action result.
 * Used by the Riwayat tab to show a toast after "Cetak Semua" finishes.
 */
export function summarizeBatch(
  action: BatchPrintAction,
  result: BatchPrintResult,
): string {
  const verb = action === 'print' ? 'dicetak' : 'disimpan';
  if (result.errors.length === 0) {
    return `✓ ${result.printed} worksheet ${verb}`;
  }
  if (result.printed === 0) {
    return `Gagal ${verb} semua worksheet (${result.errors.length} error)`;
  }
  return `${result.printed} ${verb}, ${result.errors.length} gagal`;
}
